import React, { useState, useEffect } from "react";
import {
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Paper,
  TextField,
  IconButton,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import {
  useDispatchAction,
  useReduxStore,
} from "../../../../../redux/store.hooks";
import { useBillingCompanyContext } from "../../../../../providers";
import {
  fetchAudienceListColumnsAsync,
  upsertAudienceListColumnAsync,
} from "../../../../../redux/marketCampaign/marketCampaignActions";
import { IHeaderMapping } from "../CreateAudienceRightPanel.types";
import { CreateNewHeaderDialog } from "../dialogs/CreateNewHeaderDialog";

type IStep3SetupMapping = {
  headers: string[];
  data: Record<string, any>[];
  mappings: IHeaderMapping[];
  onMappingChange: (mappings: IHeaderMapping[]) => void;
};

interface AddHeaderState {
  isAdding: boolean;
  newHeaderName: string;
}
const MANDATORY_FIELDS = ["phone"];
export const Step3SetupMapping: React.FC<IStep3SetupMapping> = (props) => {
  const { headers, data, mappings: headerMappings, onMappingChange } = props;

  const dispatch = useDispatchAction();
  const companyOutlet = useBillingCompanyContext();
  const { data: audienceListData } =
    useReduxStore("marketCampaign").audienceListColumns;
  const [addNeColumnDialog, setAddNeColumnDialog] = useState<boolean>(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [localAvailableColumns, setLocalAvailableColumns] = useState<string[]>(
    [],
  );

  const availableColumns = React.useMemo(() => {
    if (!audienceListData || !audienceListData.master_columns)
      return localAvailableColumns;

    const masterColumns = audienceListData.master_columns || [];
    const additionalColumns = audienceListData.additional_columns || [];
    const apiColumns = [...masterColumns, ...additionalColumns];

    // Combine API columns with locally added columns, removing duplicates
    const allColumnsSet: Record<string, boolean> = {};
    const allColumns: string[] = [];
    [...apiColumns, ...localAvailableColumns].forEach((col) => {
      if (!allColumnsSet[col]) {
        allColumnsSet[col] = true;
        allColumns.push(col);
      }
    });
    return allColumns;
  }, [audienceListData, localAvailableColumns]);

  // Fetch columns when dialog opens
  useEffect(() => {
    if (companyOutlet?.comapny?.company_outlet_uuid) {
      dispatch(
        fetchAudienceListColumnsAsync(
          companyOutlet.comapny.company_outlet_uuid,
          (isSuccess, data) => {
            if (!isSuccess) {
              console.log("API not available yet, using empty columns");
            }
          },
        ),
      );
    }
  }, [dispatch, companyOutlet?.comapny?.company_outlet_uuid]);

  // Initialize header mappings only once when uploaded headers change
  useEffect(() => {
    // Only initialize if we have headers, available columns, and haven't initialized yet
    // OR if headerMappings is empty (first load)
    if (
      headers.length > 0 &&
      availableColumns.length > 0 &&
      (headerMappings.length === 0 || !isInitialized)
    ) {
      const existingHeaders = new Set(headerMappings.map((m) => m.header));
      const newHeaders = headers.filter(
        (header) => !existingHeaders.has(header),
      );

      if (newHeaders.length > 0) {
        const newMappings: IHeaderMapping[] = newHeaders.map((header) => {
          // Check if this header exists in available columns
          const matchingColumn = availableColumns.find((col) => col === header);

          return {
            header,
            selectedMapping: matchingColumn ? header : "",
            isChecked: !!matchingColumn, // Only check if mapped
            isMapped: !!matchingColumn,
          };
        });

        onMappingChange([...headerMappings, ...newMappings]);
      }

      setIsInitialized(true);
    }
  }, [
    headers,
    availableColumns,
    headerMappings,
    isInitialized,
    onMappingChange,
  ]);

  // Separate mapped and unmapped headers
  const mappedHeaders = headerMappings.filter((mapping) => mapping.isMapped);
  const unmappedHeaders = headerMappings.filter((mapping) => !mapping.isMapped);

  // Get available options for dropdown (exclude already mapped options)
  const getAvailableOptions = (currentMapping: IHeaderMapping) => {
    const alreadyMappedOptions = headerMappings
      .filter(
        (mapping) =>
          mapping.isMapped &&
          mapping.selectedMapping &&
          mapping.header !== currentMapping.header,
      )
      .map((mapping) => mapping.selectedMapping);

    return availableColumns.filter(
      (column) => !alreadyMappedOptions.includes(column),
    );
  };

  // Check if mandatory fields are mapped
  const getUnmappedMandatoryFields = () => {
    const mappedMandatoryFields = headerMappings
      .filter((mapping) => mapping.isMapped && mapping.selectedMapping)
      .map((mapping) => mapping.selectedMapping);

    return MANDATORY_FIELDS.filter(
      (field) => !mappedMandatoryFields.includes(field),
    );
  };

  const unmappedMandatoryFields = getUnmappedMandatoryFields();
  const hasUnmappedMandatoryFields = unmappedMandatoryFields.length > 0;

  const handleCheckboxChange = (index: number, isMapped: boolean) => {
    const targetHeaders = isMapped ? mappedHeaders : unmappedHeaders;

    onMappingChange(
      headerMappings.map((mapping) => {
        const targetIndex = targetHeaders.findIndex(
          (m) => m.header === mapping.header,
        );
        return targetIndex === index
          ? { ...mapping, isChecked: !mapping.isChecked }
          : mapping;
      }),
    );
  };

  const handleDropdownChange = (
    index: number,
    value: string,
    isMapped: boolean,
  ) => {
    const targetHeaders = isMapped ? mappedHeaders : unmappedHeaders;

    onMappingChange(
      headerMappings.map((mapping) => {
        const targetIndex = targetHeaders.findIndex(
          (m) => m.header === mapping.header,
        );
        return targetIndex === index
          ? {
              ...mapping,
              selectedMapping: value,
              isMapped: !!value,
              isChecked: !!value,
            }
          : mapping;
      }),
    );
  };

  const handleNewColumnAddedSuccess = (newColumn: string) => {
    setAddNeColumnDialog(false);

    // Add the new column to local available columns immediately
    // This will make it appear in the dropdown options for unmapped headers
    setLocalAvailableColumns((prev) => [...prev, newColumn]);

    // DO NOT create a new mapping - the new column should only appear in dropdown options
    // Users can then select it from the dropdown for their unmapped headers
  };

  // Rest of your component remains the same...
  const renderHeaderTable = (
    headers: IHeaderMapping[],
    title: string,
    isMapped: boolean,
    data: Record<string, any>[],
  ) => (
    <>
      <Typography variant="h3" fontWeight="bold" sx={{ mb: 2 }}>
        {title}
      </Typography>
      <Paper sx={{ p: 2, border: "1px solid #e0e0e0", borderRadius: 2, mb: 2 }}>
        <Grid container spacing={2}>
          {/* Left Column - Headers */}
          <Grid item xs={4}>
            <Typography variant="body2" fontWeight="700" sx={{ mb: 1 }}>
              Uploaded Headers
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {headers.map((mapping, index) => (
                <Box
                  key={mapping.header}
                  sx={{
                    p: 1,
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                    backgroundColor: "#f9f9f9",
                    height: "56px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="body2" fontWeight="medium">
                    {mapping.header}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Grid>

          <Grid item xs={4}>
            <Typography
              variant="body2"
              textAlign="center"
              fontWeight="700"
              sx={{ mb: 1 }}
            >
              Headers Data
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {headers.map((mapping, index) => {
                const keyData = data
                  .map((item) => item[mapping.header])
                  .slice(0, 2);
                return (
                  <Box
                    key={mapping.header}
                    sx={{
                      p: 1,
                      border: "1px solid #e0e0e0",
                      borderRadius: 1,
                      backgroundColor: "#f9f9f9",
                      height: "56px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    {keyData.map((keys, idx) => (
                      <Typography key={idx} variant="body2" fontWeight="medium">
                        {keys}
                      </Typography>
                    ))}
                  </Box>
                );
              })}
            </Box>
          </Grid>

          {/* Right Column - Mapping Controls */}
          <Grid item xs={4}>
            <Typography variant="body2" fontWeight="700" sx={{ mb: 1 }}>
              Mapping Controls
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {headers.map((mapping, index) => (
                <Box
                  key={`mapping-${mapping.header}`}
                  sx={{
                    p: 1,
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                    backgroundColor: "#ffffff",
                    height: "56px",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={mapping.isChecked}
                        onChange={() => handleCheckboxChange(index, isMapped)}
                        size="small"
                        disabled={!mapping.selectedMapping}
                        sx={{
                          cursor: !mapping.selectedMapping
                            ? "not-allowed"
                            : "pointer",
                          opacity: !mapping.selectedMapping ? 0.5 : 1,
                        }}
                      />
                    }
                    label=""
                    sx={{ m: 0, minWidth: "auto" }}
                  />

                  <FormControl fullWidth size="small">
                    <Select
                      value={mapping.selectedMapping}
                      onChange={(e) =>
                        handleDropdownChange(
                          index,
                          e.target.value as string,
                          isMapped,
                        )
                      }
                      displayEmpty
                      sx={{ minWidth: "200px" }}
                    >
                      <MenuItem value="">
                        <em>
                          {getAvailableOptions(mapping).length > 0
                            ? "Select mapping"
                            : "No columns available"}
                        </em>
                      </MenuItem>
                      {getAvailableOptions(mapping).map((column) => {
                        const isPhoneNotMapped =
                          column === "phone" && hasUnmappedMandatoryFields;
                        return (
                          <MenuItem
                            key={column}
                            value={column}
                            sx={{
                              backgroundColor: isPhoneNotMapped
                                ? "#fff3cd"
                                : "inherit",
                              "&:hover": {
                                backgroundColor: isPhoneNotMapped
                                  ? "#ffeaa7"
                                  : "inherit",
                              },
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              {isPhoneNotMapped && (
                                <Typography
                                  variant="caption"
                                  color="warning.dark"
                                  fontWeight="bold"
                                >
                                  ⚠️
                                </Typography>
                              )}
                              <Typography
                                variant="body2"
                                fontWeight={
                                  isPhoneNotMapped ? "bold" : "normal"
                                }
                                color={
                                  isPhoneNotMapped ? "warning.dark" : "inherit"
                                }
                              >
                                {column}
                              </Typography>
                              {isPhoneNotMapped && (
                                <Typography
                                  variant="caption"
                                  color="warning.dark"
                                >
                                  (Required)
                                </Typography>
                              )}
                            </Box>
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>

                  {!isMapped && (
                    <IconButton
                      size="small"
                      onClick={() => setAddNeColumnDialog(true)}
                      sx={{ ml: 1 }}
                    >
                      <AddIcon />
                    </IconButton>
                  )}
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </>
  );

  return (
    <>
      {/* Mandatory Fields Warning */}
      {hasUnmappedMandatoryFields && (
        <Box
          sx={{
            p: 2,
            mb: 2,
            backgroundColor: "#fff3cd",
            border: "1px solid #ffeaa7",
            borderRadius: 2,
            borderLeft: "4px solid #f39c12",
          }}
        >
          <Typography variant="body2" color="warning.dark" fontWeight="medium">
            ⚠️ Required Mapping: Please map the following mandatory fields:{" "}
            <strong>{unmappedMandatoryFields.join(", ")}</strong>
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 0.5, display: "block" }}
          >
            These fields are required for audience processing and cannot be
            skipped.
          </Typography>
        </Box>
      )}

      {/* Mapped Headers Table */}
      {mappedHeaders.length > 0 &&
        renderHeaderTable(mappedHeaders, "Mapped Headers", true, data)}

      {/* Unmapped Headers Table */}
      {unmappedHeaders.length > 0 &&
        renderHeaderTable(unmappedHeaders, "Unmapped Headers", false, data)}

      {addNeColumnDialog && (
        <CreateNewHeaderDialog
          open={addNeColumnDialog}
          onClose={() => setAddNeColumnDialog(false)}
          onSuccess={handleNewColumnAddedSuccess}
        />
      )}
    </>
  );
};
